import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import CompetitionCard from "@/components/competition-card";
import TypewriterEffect from "@/components/typewriter-effect";
import {
  Trophy,
  Play,
  Users,
  DollarSign,
  Target,
  Star,
  UserPlus,
  Share2,
  Gift,
  Clock,
} from "lucide-react";
import heroBackground from "@assets/group-of-friends-enjoying-party-throwing-confetti-2025-02-11-16-29-33-utc_1752755783735.jpg";
import { updateSEOTags, seoConfigs } from "@/utils/seo";

export default function Landing() {
  const [filter, setFilter] = useState("all");
  const [competitionTab, setCompetitionTab] = useState("featured");
  const [, navigate] = useLocation();

  // SEO Optimization
  useEffect(() => {
    updateSEOTags(seoConfigs.home);
  }, []);

  const handleStartCompeting = () => {
    navigate("/competitions");
  };

  // Fetch featured competitions
  const { data: featuredCompetitions, isLoading: isFeaturedLoading } = useQuery({
    queryKey: ["/api/competitions/featured"],
  });

  // Fetch past competitions
  const { data: pastCompetitions, isLoading: isPastLoading } = useQuery({
    queryKey: ["/api/competitions/past"],
  });

  // Fetch global platform stats
  const { data: globalStats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ["/api/stats/overview"],
  });

  // Get current competitions based on selected tab
  const currentCompetitions = competitionTab === "featured" ? featuredCompetitions : pastCompetitions;
  const isCurrentLoading = competitionTab === "featured" ? isFeaturedLoading : isPastLoading;

  // Ensure competitions is always an array to avoid filter error
  const competitionsArray = Array.isArray(currentCompetitions) ? currentCompetitions : [];

  const filteredCompetitions = competitionsArray.filter((comp: any) => {
    if (filter === "all") return true;
    // Fix filter logic to match actual competition properties
    return comp?.type === filter || comp?.category === filter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Hero Section */}
      <section className="relative text-white py-12 md:py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${heroBackground})`,
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 hero-gradient opacity-85"></div>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 drop-shadow-lg">
              <TypewriterEffect
                text="Win Amazing Prizes"
                speed={80}
                delay={500}
                className="inline-block"
                loop={true}
                pauseDuration={3000}
              />
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-indigo-100 drop-shadow-md px-4">
              Join thousands of competitors in daily, weekly, and monthly
              competitions. Answer questions, share with friends, and win
              incredible prizes!
            </p>
          </div>
        </div>
      </section>

      {/* Competitions Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 text-gray-900">
            Competitions
          </h2>

          <Tabs value={competitionTab} onValueChange={setCompetitionTab} className="w-full">
            <TabsList className="grid w-full max-w-sm sm:max-w-md mx-auto grid-cols-2 mb-6 sm:mb-8" data-testid="competition-tabs">
              <TabsTrigger value="featured" className="flex items-center gap-2" data-testid="tab-featured">
                <Trophy className="w-4 h-4" />
                Current
              </TabsTrigger>
              <TabsTrigger value="past" className="flex items-center gap-2" data-testid="tab-past">
                <Clock className="w-4 h-4" />
                Past
              </TabsTrigger>
            </TabsList>

            <TabsContent value="featured" className="mt-0">
              <div className="text-center mb-8">
                <p className="text-gray-600 mb-4">
                  Join active competitions and compete for amazing prizes!
                </p>

                {/* Competition Type Filters */}
                <div className="flex flex-wrap gap-2 sm:gap-4 justify-center">
                  <Button
                    variant={filter === "all" ? "default" : "outline"}
                    onClick={() => setFilter("all")}
                    className="rounded-full"
                    data-testid="filter-all"
                  >
                    All
                  </Button>
                  <Button
                    variant={filter === "daily" ? "default" : "outline"}
                    onClick={() => setFilter("daily")}
                    className="rounded-full"
                    data-testid="filter-daily"
                  >
                    Daily
                  </Button>
                  <Button
                    variant={filter === "weekly" ? "default" : "outline"}
                    onClick={() => setFilter("weekly")}
                    className="rounded-full"
                    data-testid="filter-weekly"
                  >
                    Weekly
                  </Button>
                  <Button
                    variant={filter === "monthly" ? "default" : "outline"}
                    onClick={() => setFilter("monthly")}
                    className="rounded-full"
                    data-testid="filter-monthly"
                  >
                    Monthly
                  </Button>
                </div>
              </div>

              {/* Featured Competition Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8" data-testid="featured-competitions">
                {isCurrentLoading ? (
              <div className="col-span-full text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading competitions...</p>
              </div>
            ) : filteredCompetitions?.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-600">
                  No competitions found for this filter.
                </p>
              </div>
            ) : (
              filteredCompetitions?.map((competition: any) => (
                <CompetitionCard
                  key={competition.id}
                  competition={competition}
                />
              ))
            )}
              </div>
            </TabsContent>

            <TabsContent value="past" className="mt-0">
              <div className="text-center mb-8">
                <p className="text-gray-600 mb-4">
                  Browse recently completed competitions and see what you missed!
                </p>

                {/* Competition Type Filters - reuse same filter state */}
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button
                    variant={filter === "all" ? "default" : "outline"}
                    onClick={() => setFilter("all")}
                    className="rounded-full"
                    data-testid="filter-all-past"
                  >
                    All
                  </Button>
                  <Button
                    variant={filter === "daily" ? "default" : "outline"}
                    onClick={() => setFilter("daily")}
                    className="rounded-full"
                    data-testid="filter-daily-past"
                  >
                    Daily
                  </Button>
                  <Button
                    variant={filter === "weekly" ? "default" : "outline"}
                    onClick={() => setFilter("weekly")}
                    className="rounded-full"
                    data-testid="filter-weekly-past"
                  >
                    Weekly
                  </Button>
                  <Button
                    variant={filter === "monthly" ? "default" : "outline"}
                    onClick={() => setFilter("monthly")}
                    className="rounded-full"
                    data-testid="filter-monthly-past"
                  >
                    Monthly
                  </Button>
                </div>
              </div>

              {/* Past Competition Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8" data-testid="past-competitions">
                {isCurrentLoading ? (
                  <div className="col-span-full text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading past competitions...</p>
                  </div>
                ) : filteredCompetitions?.length === 0 ? (
                  <div className="col-span-full text-center py-8">
                    <p className="text-gray-600">
                      {competitionTab === "past" ? "No past competitions found for this filter." : "No competitions found for this filter."}
                    </p>
                  </div>
                ) : (
                  filteredCompetitions?.map((competition: any) => (
                    <CompetitionCard
                      key={competition.id}
                      competition={competition}
                    />
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>

          <div className="text-center mt-12">
            <Link href="/competitions">
              <Button size="lg" className="primary-gradient" data-testid="button-view-all-competitions">
                View All Competitions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Global Platform Stats */}
      {/* Platform Statistics Section - Commented Out */}
      {/*
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Platform Statistics
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of users competing for amazing prizes
            </p>
          </div>

          {statsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              <span className="ml-4 text-gray-600">Loading platform statistics...</span>
            </div>
          ) : statsError ? (
            <div className="text-center py-12">
              <p className="text-red-600">Failed to load platform statistics</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-6 text-white text-center">
                <Target className="h-12 w-12 mx-auto mb-4" />
                <div className="text-3xl font-bold mb-2">{globalStats?.totalEntries?.toLocaleString() || 0}</div>
                <div className="text-blue-100">Total Entries</div>
              </div>

              <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg p-6 text-white text-center">
                <Users className="h-12 w-12 mx-auto mb-4" />
                <div className="text-3xl font-bold mb-2">{globalStats?.totalUsers?.toLocaleString() || 0}</div>
                <div className="text-blue-100">Total Users</div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-6 text-white text-center">
                <Trophy className="h-12 w-12 mx-auto mb-4" />
                <div className="text-3xl font-bold mb-2">{globalStats?.totalWinners?.toLocaleString() || 0}</div>
                <div className="text-green-100">Total Winners</div>
              </div>
            </div>
          )}
        </div>
      </section>
      */}

      {/* Refer a Friend Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Refer a Friend & Earn More
            </h2>
            <p className="text-xl text-gray-600">
              Share the excitement and increase your winning chances together
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserPlus className="text-white h-8 w-8 sm:h-10 sm:w-10" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                1. Refer Friends
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Share your unique referral link with friends and family through
                social media, email, or messaging.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-6">
                <Share2 className="text-white h-8 w-8 sm:h-10 sm:w-10" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                2. They Join & Play
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                When your friends sign up and participate in their first
                competition, you both get rewarded.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Gift className="text-white h-8 w-8 sm:h-10 sm:w-10" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                3. Earn Bonus Entries
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Get 35 entries for every friend who joins, plus they get 35
                entries to start their journey.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-indigo-100">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Referral Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Trophy className="text-green-600 h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">
                      You Get: 35 Entries
                    </p>
                    <p className="text-sm text-gray-600">
                      Per successful referral
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Star className="text-blue-600 h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">
                      Friend Gets: 35 Entries
                    </p>
                    <p className="text-sm text-gray-600">
                      Plus regular question entries
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-900 to-blue-800 text-white hover:from-blue-950 hover:to-blue-900"
                  onClick={() => window.location.href = '/auth?tab=signup'}
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  Start Referring Friends
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Winners Say
            </h2>
            <p className="text-xl text-gray-600">
              Real testimonials from our competition winners
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* First Video Testimonial */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-lg">
              <div className="aspect-[9/16] mb-4 rounded-lg overflow-hidden bg-black">
                <iframe
                  src="https://www.youtube.com/embed/6PYV61Z9qmg"
                  title="Winner Testimonial 1"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-testimonial-1"
                ></iframe>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 font-medium">Winner Testimonial</p>
              </div>
            </div>

            {/* Second Video Testimonial */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-lg">
              <div className="aspect-[9/16] mb-4 rounded-lg overflow-hidden bg-black">
                <iframe
                  src="https://www.youtube.com/embed/gQuygXx1MA8"
                  title="Winner Testimonial 2"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-testimonial-2"
                ></iframe>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 font-medium">Winner Testimonial</p>
              </div>
            </div>

            {/* Third Video Testimonial */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-lg">
              <div className="aspect-[9/16] mb-4 rounded-lg overflow-hidden bg-black">
                <iframe
                  src="https://www.youtube.com/embed/JHTxSlFdJwM"
                  title="Winner Testimonial 3"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-testimonial-3"
                ></iframe>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 font-medium">Winner Testimonial</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link href="/testimonials">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-900 to-blue-800 text-white hover:from-blue-950 hover:to-blue-900"
                data-testid="button-view-all-testimonials"
              >
                <Trophy className="mr-2 h-5 w-5" />
                View All Testimonials
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Competitions Work
            </h2>
            <p className="text-xl text-gray-600">
              Understanding our prize competition system - fair, transparent,
              and exciting
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="text-white h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    1. Join a Competition
                  </h3>
                  <p className="text-gray-600">
                    Browse our daily, weekly, and monthly competitions. Each has
                    different prizes and entry requirements. Simply click "Enter
                    Competition" to join.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="text-white h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    2. Answer Questions & Earn Entries
                  </h3>
                  <p className="text-gray-600">
                    Each answer gives you 1 entry into the prize draw.
                    All answers count as participation - you get 1 entry per answer.
                    The more questions you answer, the higher your chances!
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Share2 className="text-white h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    3. Boost Your Chances
                  </h3>
                  <p className="text-gray-600">
                    Share competitions on social media for bonus entries. Refer
                    friends and you both get 1 entry each. Every share and referral
                    increases your winning odds.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trophy className="text-white h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    4. Random Winner Selection
                  </h3>
                  <p className="text-gray-600">
                    At the competition end time, winners are selected randomly
                    from all entries. More entries = better chances. Winners are
                    notified immediately and prizes are delivered fast.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-indigo-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Competition Types
              </h3>

              <div className="space-y-6">
                <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
                  <h4 className="font-bold text-gray-900 mb-2">
                    Daily Competitions
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Quick prizes, fast turnaround
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 5-10 questions per competition</li>
                    <li>• Winners announced same day</li>

                  </ul>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-blue-900">
                  <h4 className="font-bold text-gray-900 mb-2">
                    Weekly Competitions
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    More questions, bigger prizes
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 15-25 questions throughout the week</li>
                    <li>• Winners announced every Sunday</li>

                  </ul>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                  <h4 className="font-bold text-gray-900 mb-2">
                    Monthly Competitions
                  </h4>

                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 30+ questions across the month</li>
                    <li>• Grand finale at month end</li>

                  </ul>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                  onClick={handleStartCompeting}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Start Competing Now
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6">
            <div className="flex items-start">
              <Star className="h-6 w-6 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">
                  Fair Play Guarantee
                </h4>
                <p className="text-gray-700">
                  Our random selection system is completely transparent and
                  verifiable. Every participant has a fair chance based on their
                  entries. No hidden algorithms or favoritism - just pure chance
                  based on your participation level.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}